"use client";

import { useState } from "react";

import {
  deleteIdentificationEnvSiv,
  editIdentificationEnvSiv,
  addIdentificationEnvSiv,
} from "@/app/lib/actions/identification-env-siv";

import { ItemContent, ItemTitle } from "@/components/ui/item";
import {
  ItemsListCard,
  ItemsListCardSkeleton,
} from "@/components/backoffice/items-list-card";

import {
  useIdentificationEnvSivQuery,
  invalidateIdentificationEnvSivQuery,
} from "@/lib/queries/identification-env-siv";

import { useQueryClient } from "@tanstack/react-query";
import * as z from "zod";

import {
  addIdentificationEnvSivSchema,
  editIdentificationEnvSivSchema,
} from "@/lib/schemas/identification-env-siv";

import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";

import { toast } from "sonner";

type IdentificationEnvSiv = {
  id: number;
  environment: string;
  sivIdentifier: string;

  marqueJnl: string;
  codeConcentrateurJnl: string;
  codePartenaireJnl: string;

  isActive: boolean;
};

function IdentificationEnvSivItemsListCard() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("");

  const query = useIdentificationEnvSivQuery({
    sort: { environment: "asc" },
  });

  const handleCreate = async (
    data: z.infer<typeof addIdentificationEnvSivSchema>,
  ) => {
    const response = await addIdentificationEnvSiv(data);

    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Paramètre créé.");
      invalidateIdentificationEnvSivQuery(queryClient);
    }
  };

  const handleUpdate = async (
    data: z.infer<typeof editIdentificationEnvSivSchema>,
  ) => {
    const response = await editIdentificationEnvSiv(data);

    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Paramètre modifié.");
      invalidateIdentificationEnvSivQuery(queryClient);
    }
  };

  const handleDelete = async (id: { id: number }) => {
    const response = await deleteIdentificationEnvSiv(id);

    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Paramètre supprimé.");
      invalidateIdentificationEnvSivQuery(queryClient);
    }
  };

  if (query.isPending) return <ItemsListCardSkeleton />;

  const identificationEnvSiv = query.data?.identificationEnvSiv ?? [];

  const ENV_OPTIONS = [
    { label: "PRELIVE", value: "PRELIVE" },
    { label: "PRODUCTION", value: "PRODUCTION" },
    { label: "TEST", value: "TEST" },
  ];
  return (
    <ItemsListCard<IdentificationEnvSiv>
      title="Identification ENV SIV"
      items={identificationEnvSiv}
      createSchema={addIdentificationEnvSivSchema}
      editSchema={editIdentificationEnvSivSchema}
      defaultCreateValues={{
        environment: "",
        sivIdentifier: "",
        marqueJnl: "",
        codeConcentrateurJnl: "",
        codePartenaireJnl: "",
        isActive: true,
      }}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      renderItem={(item) => (
        <ItemContent>
          <ItemTitle>{item.environment}</ItemTitle>
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-700">
            <div className="flex items-center gap-1">
              <span className="text-slate-500">COD-SIV :</span>
              <span className="font-mono">{item.sivIdentifier}</span>
            </div>

            <span className="text-slate-300">|</span>

            <div className="flex items-center gap-1">
              <span className="text-slate-500">Marque JNL :</span>
              <span className="font-mono">{item.marqueJnl}</span>
            </div>

            <span className="text-slate-300">|</span>

            <div className="flex items-center gap-1">
              <span className="text-slate-500">COD-CONC-JNL :</span>
              <span className="font-mono">{item.codeConcentrateurJnl}</span>
            </div>

            <span className="text-slate-300">|</span>

            <div className="flex items-center gap-1">
              <span className="text-slate-500">COD-PARTNR-JNL :</span>
              <span className="font-mono">{item.codePartenaireJnl}</span>
            </div>
          </div>
        </ItemContent>
      )}
      renderFormFields={(form) => (
        <FieldGroup>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <form.Field name="environment">
              {(field) => (
                <Field>
                  <FieldLabel>Environnement</FieldLabel>

                  <Select
                    value={field.state.value}
                    onValueChange={field.handleChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>

                    <SelectContent>
                      {ENV_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>

            <form.Field name="sivIdentifier">
              {(field) => (
                <Field>
                  <FieldLabel>Identifiant SIV</FieldLabel>

                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    maxLength={100}
                  />

                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>

            <form.Field name="marqueJnl">
              {(field) => (
                <Field>
                  <FieldLabel>Marque JNL</FieldLabel>

                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    maxLength={100}
                  />

                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>

            <form.Field name="codeConcentrateurJnl">
              {(field) => (
                <Field>
                  <FieldLabel>Code concentrateur</FieldLabel>

                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    maxLength={20}
                  />

                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>

            <form.Field name="codePartenaireJnl">
              {(field) => (
                <Field>
                  <FieldLabel>Code partenaire</FieldLabel>

                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    maxLength={20}
                  />

                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>
          </div>

          <form.Field name="isActive">
            {(field) => (
              <Field className="mt-2 flex items-center justify-between rounded-md border p-4">
                <FieldLabel>Statut actif</FieldLabel>

                <Switch
                  checked={field.state.value}
                  onCheckedChange={field.handleChange}
                />
              </Field>
            )}
          </form.Field>
        </FieldGroup>
      )}
    />
  );
}

export { IdentificationEnvSivItemsListCard };
